import { SupplierEmissionsReportEvidenceType } from '@/features/suppliers/hooks/supplierEmissionsReport'
import { ElectricityProductionMethod } from '@/common'
import { isReportingPeriodEnded } from '@artem-cbam-declarants/shared'
import { z } from 'zod'

export type SupplierEmissionsReportFormData = {
  periodId: string
  cnCode: string
  aggregatedGoods: string
  productionMethod: string
  goodsDescription: string
  specificDirectEmbeddedEmissions: string
  specificIndirectEmbeddedEmissions: string
  electricityConsumptionMWh: string
  electricityEmissionFactor: string
  electricityProductionMethod: ElectricityProductionMethod | ''
  provideSIEEDirectly: boolean
  evidenceFileName: string
  evidenceType: SupplierEmissionsReportEvidenceType | ''
  startDate: string
  endDate: string
}

export type ValidateSupplierEmissionsReportFormOptions = {
  isEditMode?: boolean
  originalPeriodId?: string
}

const isPeriodEligibleForSave = (
  periodId: string,
  options?: ValidateSupplierEmissionsReportFormOptions
): boolean => {
  if (!periodId.trim()) {
    return false
  }

  if (isReportingPeriodEnded(periodId)) {
    return true
  }

  return Boolean(
    options?.isEditMode && options.originalPeriodId && periodId === options.originalPeriodId
  )
}

const supplierEmissionsReportFormSchema = z
  .object({
    periodId: z.string().min(1),
    goods: z.array(
      z.object({
        productionMethodCode: z.string().min(1),
        cnCode: z.string().min(1),
        goodsDescription: z.string().min(1)
      })
    ),
    specificDirectEmbeddedEmissions: z.number(),
    specificIndirectEmbeddedEmissions: z.number(),
    electricityConsumptionMWh: z.number(),
    electricityProductionMethod: z.enum([
      ElectricityProductionMethod.GRID,
      ElectricityProductionMethod.PPA,
      ElectricityProductionMethod.DIRECT_TECHNICAL_LINK
    ]),
    evidenceFileName: z.string().optional(),
    evidenceType: z
      .enum([
        SupplierEmissionsReportEvidenceType.TED01,
        SupplierEmissionsReportEvidenceType.TED02,
        SupplierEmissionsReportEvidenceType.TED03,
        SupplierEmissionsReportEvidenceType.TED04,
        SupplierEmissionsReportEvidenceType.TED05
      ])
      .optional()
  })
  .refine(
    data => {
      return (
        (data.evidenceFileName === undefined && data.evidenceType === undefined) ||
        (data.evidenceFileName !== undefined && data.evidenceType !== undefined)
      )
    },
    {
      error: 'Evidence filename and type must both be provided or both be omitted'
    }
  )

export const validateSupplierEmissionsReportForm = (
  data: SupplierEmissionsReportFormData,
  options?: ValidateSupplierEmissionsReportFormOptions
) => {
  const validatedData = {
    periodId: data.periodId,
    goods: [
      {
        productionMethodCode: data.productionMethod,
        cnCode: data.cnCode,
        goodsDescription: data.goodsDescription
      }
    ],
    specificDirectEmbeddedEmissions: data.specificDirectEmbeddedEmissions
      ? Number(data.specificDirectEmbeddedEmissions)
      : undefined,
    specificIndirectEmbeddedEmissions: data.specificIndirectEmbeddedEmissions
      ? Number(data.specificIndirectEmbeddedEmissions)
      : undefined,
    electricityConsumptionMWh: data.electricityConsumptionMWh
      ? Number(data.electricityConsumptionMWh)
      : undefined,
    electricityProductionMethod: data.electricityProductionMethod || undefined,
    evidenceFileName: data.evidenceFileName || undefined,
    evidenceType: data.evidenceType || undefined
  }

  return supplierEmissionsReportFormSchema
    .refine(({ periodId }) => isPeriodEligibleForSave(periodId, options), {
      error: 'Reporting period has not yet ended',
      path: ['periodId']
    })
    .safeParse(validatedData)
}
